import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Safe localStorage wrapper.
 * Handles two failure modes:
 *   1. localStorage not available (private/incognito may throw on access).
 *   2. QuotaExceededError from large base64 image payloads.
 *      → strips image blobs from the payload and retries with text-only data.
 */
const safeStorage = {
  getItem: (name: string): string | null => {
    try { return localStorage.getItem(name); } catch { return null; }
  },
  setItem: (name: string, value: string): void => {
    try {
      localStorage.setItem(name, value);
    } catch {
      // QuotaExceededError — retry without image blobs
      try {
        const parsed = JSON.parse(value) as { state?: Record<string, unknown> };
        if (parsed?.state) {
          parsed.state.logoDataUrl    = null;
          parsed.state.logoDarkDataUrl = null;
          parsed.state.faviconDataUrl = null;
        }
        localStorage.setItem(name, JSON.stringify(parsed));
      } catch { /* give up silently */ }
    }
  },
  removeItem: (name: string): void => {
    try { localStorage.removeItem(name); } catch { /* ignore */ }
  },
};

export interface BrandingState {
  appName:        string;
  primaryHex:     string;
  /** The highlight colour — orange against a charcoal primary, for DL. */
  accentHex:      string;
  logoDataUrl:    string | null;
  /** A second logo for the dark theme; falls back to the light one. */
  logoDarkDataUrl: string | null;
  faviconDataUrl: string | null;
  twitterUrl:     string;
  linkedinUrl:    string;
  facebookUrl:    string;
  copyrightText:  string;
  setAppName:        (name: string)       => void;
  setPrimaryHex:     (hex: string)        => void;
  setAccentHex:      (hex: string)        => void;
  setLogoDataUrl:    (url: string | null) => void;
  setLogoDarkDataUrl: (url: string | null) => void;
  setFaviconDataUrl: (url: string | null) => void;
  setTwitterUrl:     (url: string)        => void;
  setLinkedinUrl:    (url: string)        => void;
  setFacebookUrl:    (url: string)        => void;
  setCopyrightText:  (text: string)       => void;
  /** Hydrate branding from the server (called once on app start). */
  initBranding: () => Promise<void>;
}

export const useBrandingStore = create<BrandingState>()(
  persist(
    (set) => ({
      appName:        'GRC Platform',
      primaryHex:     '#2563eb',
      accentHex:      '#f97316',
      logoDataUrl:    null,
      logoDarkDataUrl: null,
      faviconDataUrl: null,
      twitterUrl:     '',
      linkedinUrl:    '',
      facebookUrl:    '',
      copyrightText:  '',
      setAppName:        (name) => set({ appName: name }),
      setPrimaryHex:     (hex)  => set({ primaryHex: hex }),
      setAccentHex:      (hex)  => set({ accentHex: hex }),
      setLogoDataUrl:    (url)  => set({ logoDataUrl: url }),
      setLogoDarkDataUrl: (url) => set({ logoDarkDataUrl: url }),
      setFaviconDataUrl: (url)  => set({ faviconDataUrl: url }),
      setTwitterUrl:     (url)  => set({ twitterUrl: url }),
      setLinkedinUrl:    (url)  => set({ linkedinUrl: url }),
      setFacebookUrl:    (url)  => set({ facebookUrl: url }),
      setCopyrightText:  (text) => set({ copyrightText: text }),
      initBranding: async () => {
        try {
          const res = await fetch('/api/v1/settings/branding');
          if (!res.ok) return;
          const { data } = await res.json() as { data: {
            appName: string; primaryHex: string; accentHex?: string;
            logoDataUrl: string | null; logoDarkDataUrl?: string | null; faviconDataUrl: string | null;
            twitterUrl: string | null; linkedinUrl: string | null;
            facebookUrl: string | null; copyrightText: string | null;
          } };
          set({
            appName:        data.appName        ?? 'GRC Platform',
            primaryHex:     data.primaryHex     ?? '#2563eb',
            accentHex:      data.accentHex      ?? '#f97316',
            logoDataUrl:    data.logoDataUrl     ?? null,
            logoDarkDataUrl: data.logoDarkDataUrl ?? null,
            faviconDataUrl: data.faviconDataUrl  ?? null,
            twitterUrl:     data.twitterUrl      ?? '',
            linkedinUrl:    data.linkedinUrl     ?? '',
            facebookUrl:    data.facebookUrl     ?? '',
            copyrightText:  data.copyrightText   ?? '',
          });
        } catch {
          // Server unavailable — keep localStorage / default values
        }
      },
    }),
    {
      name:    'grc-branding',
      storage: createJSONStorage(() => safeStorage),
    },
  ),
);
